import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback, useMemo } from "react";
import LogLayout from "../components/LogLayout";
import SectionTabs, { FINANCE_TABS } from "../components/SectionTabs";
import {
  Building2, AlertTriangle, CheckCircle2, TrendingUp, CreditCard,
  Plus, Trash2, RefreshCw, Target, BarChart3, Zap, Info, Download, FileText,
  ArrowUpRight, ArrowDownRight, PieChart, Wallet, Calendar, Bell, ChevronLeft, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, PieChart as RechartsPie, Pie, Legend, Area, AreaChart
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));

const PERIODS = [
  { value: "1m", label: "Ce mois" },
  { value: "3m", label: "3 mois" },
  { value: "6m", label: "6 mois" },
  { value: "12m", label: "12 mois" },
  { value: "all", label: "Tout" },
];

const ALERT_CFG = {
  CRITICAL: { bg: "bg-red-500/100/8 border-red-500/25", text: "text-red-400" },
  DANGER: { bg: "bg-red-500/100/8 border-red-500/25", text: "text-red-400" },
  WARNING: { bg: "bg-amber-500/100/8 border-amber-500/25", text: "text-amber-400" },
  ATTENTION: { bg: "bg-orange-500/8 border-orange-500/25", text: "text-orange-400" },
};

const PIE_COLORS = ["#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "#14b8a6", "#6366f1"];


function OverdueBanner({ forecasts, fmt }) {
  const overdueItems = forecasts.filter(f => f.is_overdue);
  if (overdueItems.length === 0) return null;
  const totalOverdue = overdueItems.reduce((s, f) => s + (f.expected_amount || 0), 0);
  const maxDays = Math.max(...overdueItems.map(f => f.days_overdue || 0));
  const insurers = [...new Set(overdueItems.map(f => f.insurer_name))];

  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 animate-pulse-slow" data-testid="overdue-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-bold text-red-800 flex items-center gap-2">
            <Bell className="w-4 h-4 text-red-600 animate-bounce" />
            {overdueItems.length} encaissement{overdueItems.length > 1 ? "s" : ""} en retard !
          </h4>
          <p className="text-xs text-red-700 mt-1">
            <span className="font-bold">{fmt(totalOverdue)} FCFA</span> non encaisses — retard max: <span className="font-bold">{maxDays} jour{maxDays > 1 ? "s" : ""}</span>
          </p>
          <p className="text-[10px] text-red-500 mt-1">
            Assureurs concernes : {insurers.join(", ")}
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {overdueItems.slice(0, 5).map((item, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-[10px] font-semibold text-red-800 border border-red-200">
                <AlertTriangle className="w-3 h-3" />
                {item.insurer_name} — {fmt(item.expected_amount)} FCFA ({item.days_overdue}j)
              </span>
            ))}
            {overdueItems.length > 5 && (
              <span className="text-[10px] text-red-500">+{overdueItems.length - 5} autre(s)</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EncaissementsCalendar({ forecasts, fmt }) {
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const monthNames = ["Janvier", "Fevrier", "Mars", "Avril", "Mai", "Juin", "Juillet", "Aout", "Septembre", "Octobre", "Novembre", "Decembre"];
  const dayNames = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

  const prevMonth = () => {
    setCalendarMonth(p => p.month === 0 ? { year: p.year - 1, month: 11 } : { year: p.year, month: p.month - 1 });
  };
  const nextMonth = () => {
    setCalendarMonth(p => p.month === 11 ? { year: p.year + 1, month: 0 } : { year: p.year, month: p.month + 1 });
  };

  const calendarDays = useMemo(() => {
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    return days;
  }, [calendarMonth]);

  const forecastsByDate = useMemo(() => {
    const map = {};
    for (const fc of forecasts) {
      const dateKey = fc.availability_date || fc.expected_date;
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(fc);
    }
    return map;
  }, [forecasts]);

  const todayStr = new Date().toISOString().split("T")[0];

  // Count overdue for current month view
  const monthOverdue = useMemo(() => {
    let count = 0, amount = 0;
    for (const fc of forecasts) {
      const dateKey = fc.availability_date || fc.expected_date;
      const { year, month } = calendarMonth;
      const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
      if (dateKey.startsWith(monthStr) && fc.is_overdue) {
        count++;
        amount += fc.expected_amount || 0;
      }
    }
    return { count, amount };
  }, [forecasts, calendarMonth]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="encaissements-calendar">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-sky-600" />
          Calendrier des encaissements
          {monthOverdue.count > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-[10px] font-bold text-red-700 border border-red-200 animate-pulse" data-testid="calendar-overdue-badge">
              <AlertTriangle className="w-3 h-3" /> {monthOverdue.count} en retard
            </span>
          )}
        </h4>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-1 rounded hover:bg-slate-100"><ChevronLeft className="w-4 h-4 text-slate-500" /></button>
          <span className="text-sm font-medium text-slate-700 min-w-[140px] text-center">
            {monthNames[calendarMonth.month]} {calendarMonth.year}
          </span>
          <button onClick={nextMonth} className="p-1 rounded hover:bg-slate-100"><ChevronRight className="w-4 h-4 text-slate-500" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
        {dayNames.map(d => (
          <div key={d} className="bg-slate-50 py-2 text-center text-[10px] font-semibold text-slate-500">{d}</div>
        ))}
        {calendarDays.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} className="bg-white p-1 min-h-[60px]" />;
          const dateStr = `${calendarMonth.year}-${String(calendarMonth.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const dayForecasts = forecastsByDate[dateStr] || [];
          const totalAmount = dayForecasts.reduce((s, f) => s + (f.expected_amount || 0), 0);
          const isToday = dateStr === todayStr;
          const isPast = dateStr < todayStr;
          const hasOverdue = dayForecasts.some(f => f.is_overdue);
          const overdueCount = dayForecasts.filter(f => f.is_overdue).length;
          return (
            <div key={dateStr} className={`p-1.5 min-h-[60px] relative group transition-colors ${
              hasOverdue ? "bg-red-50 border-l-2 border-red-400" : "bg-white"
            } ${isToday ? "ring-2 ring-sky-400 ring-inset" : ""}`}>
              <div className="flex items-center justify-between">
                <span className={`text-[11px] font-medium ${isToday ? "text-sky-600 font-bold" : isPast ? "text-slate-300" : "text-slate-700"}`}>{day}</span>
                {hasOverdue && (
                  <span className="flex items-center justify-center w-4 h-4 rounded-full bg-red-500 animate-pulse" data-testid={`overdue-indicator-${dateStr}`}>
                    <AlertTriangle className="w-2.5 h-2.5 text-white" />
                  </span>
                )}
              </div>
              {dayForecasts.length > 0 && (
                <div className={`mt-1 rounded px-1 py-0.5 ${hasOverdue ? "bg-red-100 border border-red-200" : totalAmount > 0 ? "bg-emerald-100" : "bg-slate-100"}`}>
                  <p className={`text-[9px] font-bold ${hasOverdue ? "text-red-700" : "text-emerald-700"}`}>
                    {fmt(totalAmount)}
                  </p>
                  <p className={`text-[8px] ${hasOverdue ? "text-red-500 font-semibold" : "text-slate-500"}`}>
                    {hasOverdue ? `${overdueCount} retard${overdueCount > 1 ? "s" : ""}` : `${dayForecasts.length} fact.`}
                  </p>
                </div>
              )}
              {dayForecasts.length > 0 && (
                <div className="absolute left-0 top-full mt-1 z-50 w-56 bg-white border border-slate-200 rounded-lg shadow-xl p-3 hidden group-hover:block">
                  <p className="text-[10px] font-bold text-slate-700 mb-2">{new Date(dateStr + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}</p>
                  {dayForecasts.map((fc, j) => (
                    <div key={j} className={`flex justify-between items-center py-1 border-b border-slate-100 last:border-0 ${fc.is_overdue ? "bg-red-50 -mx-1 px-1 rounded" : ""}`}>
                      <div>
                        <p className={`text-[10px] font-medium ${fc.is_overdue ? "text-red-800" : "text-slate-800"}`}>
                          {fc.is_overdue && <AlertTriangle className="w-3 h-3 text-red-500 inline mr-1" />}
                          {fc.insurer_name}
                        </p>
                        <p className="text-[9px] text-slate-400">{fc.invoice_number || fc.invoice_id?.slice(0, 8)}</p>
                        {fc.is_overdue && <p className="text-[9px] text-red-500 font-semibold">Retard: {fc.days_overdue}j</p>}
                      </div>
                      <span className={`text-[10px] font-bold ${fc.is_overdue ? "text-red-600" : "text-emerald-600"}`}>{fmt(fc.expected_amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 justify-center">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" /><span className="text-[10px] text-slate-500">Encaissement prevu</span></div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-100 border-l-2 border-red-400 flex items-center justify-center">
            <AlertTriangle className="w-2 h-2 text-red-500" />
          </div>
          <span className="text-[10px] text-red-600 font-semibold">En retard (non effectif)</span>
        </div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded ring-2 ring-sky-400" /><span className="text-[10px] text-slate-500">Aujourd'hui</span></div>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1e293b] border border-white/10 rounded-xl p-3 shadow-2xl">
      <p className="text-xs font-semibold text-white mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="text-white font-semibold">{fmt(p.value)} FCFA</span>
        </div>
      ))}
    </div>
  );
}

export default function LogCréancesPage() {
  const [data, setData] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [period, setPeriod] = useState("all");
  const [activeTab, setActiveTab] = useState("créances");
  const [loading, setLoading] = useState(true);
  const [showCeiling, setShowCeiling] = useState(false);
  const [ceilingForm, setCeilingForm] = useState({ insurer_name: "", ceiling_amount: "", alert_threshold_percent: 80 });
  const [salesForm, setSalesForm] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [registeredInsurers, setRegisteredInsurers] = useState([]);
  const [objective, setObjective] = useState(null);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState("80");
  const [savingTarget, setSavingTarget] = useState(false);
  const [forecastData, setForecastData] = useState(null);
  const [showForecastForm, setShowForecastForm] = useState(false);
  const [forecastForm, setForecastForm] = useState({ invoice_id: "", expected_date: "", notes: "" });

  useEffect(() => {
    axios.get(`${API}/log/invoices/insurers`, { headers: getHeaders() })
      .then(r => setRegisteredInsurers(r.data.insurers || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const [recv, tres, evo, obj, fc] = await Promise.all([
        axios.get(`${API}/log/receivables/summary?period=${period}`, { headers: getHeaders() }),
        axios.get(`${API}/log/receivables/treasury-impact?period=${period}`, { headers: getHeaders() }),
        axios.get(`${API}/log/receivables/monthly-evolution`, { headers: getHeaders() }),
        axios.get(`${API}/log/receivables/objectives`, { headers: getHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/log/encaissements/summary`, { headers: getHeaders() }).catch(() => ({ data: null })),
      ]);
      setData(recv.data);
      setTreasury(tres.data);
      setEvolution(evo.data);
      if (obj.data) {
        setObjective(obj.data);
        setTargetInput(String(obj.data.objective?.target_rate || 80));
      }
      if (fc.data) setForecastData(fc.data);
    } catch { }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const saveCeiling = async () => {
    if (!ceilingForm.insurer_name || !ceilingForm.ceiling_amount) return;
    try {
      await axios.post(`${API}/log/receivables/ceilings`, {
        insurer_name: ceilingForm.insurer_name,
        ceiling_amount: parseFloat(ceilingForm.ceiling_amount),
        alert_threshold_percent: parseFloat(ceilingForm.alert_threshold_percent) || 80,
      }, { headers: getHeaders() });
      toast.success("Plafond enregistré");
      setCeilingForm({ insurer_name: "", ceiling_amount: "", alert_threshold_percent: 80 });
      setShowCeiling(false);
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const saveSales = async () => {
    if (!salesForm) return;
    try {
      await axios.post(`${API}/log/receivables/monthly-sales`, { amount: parseFloat(salesForm) }, { headers: getHeaders() });
      toast.success(`CA mensuel mis à jour: ${fmt(parseFloat(salesForm))} FCFA`);
      setSalesForm("");
      await load();
    } catch (e) { toast.error("Erreur lors de l'enregistrement du CA"); }
  };

  const saveTarget = async () => {
    const rate = parseFloat(targetInput);
    if (!rate || rate < 1 || rate > 100) return toast.error("Objectif invalide (1-100%)");
    setSavingTarget(true);
    try {
      await axios.post(`${API}/log/receivables/objectives`, { target_rate: rate, alert_enabled: true }, { headers: getHeaders() });
      toast.success(`Objectif de recouvrement fixé à ${rate}%`);
      setEditingTarget(false);
      await load();
    } catch (e) { toast.error("Erreur"); }
    finally { setSavingTarget(false); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/log/receivables/export-excel?period=${period}`, {
        headers: getHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `créances_assureurs_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch (e) { toast.error("Erreur lors de l'export"); }
    finally { setExporting(false); }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await axios.get(`${API}/log/receivables/export-pdf?period=${period}`, {
        headers: getHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `créances_assureurs_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export PDF téléchargé");
    } catch (e) { toast.error("Erreur lors de l'export PDF"); }
    finally { setExportingPdf(false); }
  };

  const createForecast = async () => {
    if (!forecastForm.invoice_id || !forecastForm.expected_date) return;
    try {
      await axios.post(`${API}/log/encaissements/forecast`, forecastForm, { headers: getHeaders() });
      toast.success("Prévision d'encaissement créée");
      setShowForecastForm(false);
      setForecastForm({ invoice_id: "", expected_date: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return (
    <LogLayout title="Finance — Créances & Reçus" subtitle="Vue complète : montants dus + paiements reçus">
      <SectionTabs tabs={FINANCE_TABS} />
      <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
    </LogLayout>
  );

  const insurers = data?.insurers || [];
  const totals = data?.totals || {};
  const alerts = data?.alerts || [];

  const chartData = insurers.slice(0, 10).map(i => ({
    name: i.insurer_name?.length > 15 ? i.insurer_name.substring(0, 15) + "..." : i.insurer_name,
    créances: i.remaining,
    plafond: i.ceiling_amount,
  }));

  return (
    <LogLayout title="Finance — Créances & Reçus" subtitle="À encaisser : montants dus, plafonds, vieillissement, alertes">
      <SectionTabs tabs={FINANCE_TABS} />
      <div data-testid="créances-page" className="space-y-5">
        {/* Tab selector + Period filter */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* Tabs */}
            <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
              <button onClick={() => setActiveTab("créances")} data-testid="tab-créances"
                className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${activeTab === "créances" ? "bg-sky-500 text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                <CreditCard className="w-3.5 h-3.5" /> Créances
              </button>
              <button onClick={() => setActiveTab("trésorerie")} data-testid="tab-trésorerie"
                className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${activeTab === "trésorerie" ? "bg-violet-500 text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                <Wallet className="w-3.5 h-3.5" /> Trésorerie
              </button>
              <button onClick={() => setActiveTab("encaissements")} data-testid="tab-encaissements"
                className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${activeTab === "encaissements" ? "bg-emerald-500 text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                <Calendar className="w-3.5 h-3.5" /> Encaissements
              </button>
            </div>
            {/* Period */}
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
              {PERIODS.map(p => (
                <button key={p.value} onClick={() => setPeriod(p.value)} data-testid={`period-${p.value}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${period === p.value ? "bg-sky-500 text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={exportExcel} disabled={exporting} variant="outline" size="sm" className="border-emerald-500/20 text-emerald-400 text-xs gap-1" data-testid="export-excel-btn">
              {exporting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Excel
            </Button>
            <Button onClick={exportPdf} disabled={exportingPdf} variant="outline" size="sm" className="border-red-400/20 text-red-400 text-xs gap-1" data-testid="export-pdf-btn">
              {exportingPdf ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} PDF
            </Button>
            <Button onClick={() => setShowCeiling(!showCeiling)} variant="outline" size="sm" className="border-amber-500/20 text-amber-400 text-xs gap-1">
              <Target className="w-3.5 h-3.5" /> Plafond
            </Button>
            <Button onClick={load} variant="outline" size="sm" className="border-white/10 text-slate-400 text-xs gap-1">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* KPIs — always visible */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <div className="bg-white border border-red-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2"><ArrowUpRight className="w-4 h-4 text-red-500" /><span className="text-[11px] text-slate-500">Reste à recouvrer</span></div>
            <p className="text-xl font-bold text-red-600" data-testid="kpi-receivables">{fmt(totals.total_receivables)} <span className="text-xs text-slate-400">FCFA</span></p>
          </div>
          <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2"><ArrowDownRight className="w-4 h-4 text-emerald-500" /><span className="text-[11px] text-slate-500">Déjà recouvré</span></div>
            <p className="text-xl font-bold text-emerald-600" data-testid="kpi-recovered">{fmt(totals.total_paid)} <span className="text-xs text-slate-400">FCFA</span></p>
          </div>
          <div className="bg-white border border-sky-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-sky-500" /><span className="text-[11px] text-slate-500">Assureurs</span></div>
            <p className="text-xl font-bold text-slate-900">{totals.total_insurers}</p>
          </div>
          {treasury && (
            <>
              {/* Ratio de perte - Non recouvré / Total facturé */}
              <div className={`border rounded-xl p-4 shadow-sm ${treasury.loss_alert === "CRITICAL" ? "bg-red-50 border-red-200" : treasury.loss_alert === "WARNING" ? "bg-amber-50 border-amber-200" : treasury.loss_alert === "ATTENTION" ? "bg-orange-50 border-orange-200" : "bg-white border-slate-200"}`} data-testid="kpi-loss-ratio">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-500" /><span className="text-[11px] text-slate-500">Taux de non-recouvrement</span></div>
                <p className={`text-xl font-bold ${treasury.loss_ratio > 40 ? "text-red-600" : treasury.loss_ratio > 20 ? "text-amber-600" : "text-emerald-600"}`}>
                  {treasury.loss_ratio}%
                </p>
                <p className="text-[9px] text-slate-500 mt-1">{fmt(treasury.total_outstanding)} / {fmt(treasury.total_billed)} FCFA</p>
              </div>
              {/* Ratio Créances / CA mensuel */}
              <div className={`border rounded-xl p-4 shadow-sm ${treasury.sales_alert === "CRITICAL" || treasury.sales_alert === "DANGER" ? "bg-red-50 border-red-200" : treasury.sales_alert === "WARNING" ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`} data-testid="kpi-sales-ratio">
                <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-violet-500" /><span className="text-[11px] text-slate-500">Créances / CA mensuel</span></div>
                <p className={`text-xl font-bold ${treasury.sales_vs_credit_ratio >= 75 ? "text-red-600" : treasury.sales_vs_credit_ratio >= 50 ? "text-amber-600" : "text-emerald-600"}`}>
                  {treasury.sales_vs_credit_ratio}%
                </p>
                <p className="text-[9px] text-slate-500 mt-1">CA: {fmt(treasury.monthly_sales)} FCFA{!treasury.estimated_sales_set ? " (auto)" : ""}</p>
              </div>
              {/* Taux de recouvrement */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2"><Wallet className="w-4 h-4 text-amber-500" /><span className="text-[11px] text-slate-500">Taux de recouvrement</span></div>
                <p className="text-xl font-bold text-amber-600">
                  {treasury.total_billed > 0 ? Math.round(treasury.total_recovered / treasury.total_billed * 100) : 0}%
                </p>
                <p className="text-[9px] text-slate-500 mt-1">{fmt(treasury.total_recovered)} / {fmt(treasury.total_billed)} FCFA</p>
              </div>
            </>
          )}
        </div>

        {/* Recovery Objective Gauge */}
        {objective && (
          <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5" data-testid="recovery-objective">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Target className="w-4 h-4 text-cyan-400" /> Objectif de recouvrement
              </h3>
              {editingTarget ? (
                <div className="flex items-center gap-2">
                  <input type="number" value={targetInput} onChange={e => setTargetInput(e.target.value)}
                    className="w-20 bg-slate-950 border border-cyan-500/30 rounded-lg px-2 py-1 text-sm text-white text-center focus:outline-none"
                    min={1} max={100} data-testid="target-input" />
                  <span className="text-xs text-slate-500">%</span>
                  <Button size="sm" onClick={saveTarget} disabled={savingTarget} className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs h-7 px-3" data-testid="save-target-btn">
                    {savingTarget ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Enregistrer"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingTarget(false)} className="text-slate-500 text-xs h-7 px-2">Annuler</Button>
                </div>
              ) : (
                <button onClick={() => setEditingTarget(true)} className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors" data-testid="edit-target-btn">
                  Modifier l'objectif ({objective.objective.target_rate}%)
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Gauge */}
              <div className="col-span-1 flex flex-col items-center justify-center">
                <div className="relative w-32 h-32">
                  <svg viewBox="0 0 36 36" className="w-32 h-32 transform -rotate-90">
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke={objective.performance.status === "success" ? "#10B981" : objective.performance.status === "warning" ? "#F59E0B" : "#EF4444"}
                      strokeWidth="3" strokeDasharray={`${objective.performance.current_rate}, 100`} strokeLinecap="round" />
                    {/* Target marker */}
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke="rgba(6,182,212,0.5)" strokeWidth="3"
                      strokeDasharray={`0.5, ${objective.objective.target_rate - 0.25}, 0.5, 100`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-2xl font-bold ${objective.performance.status === "success" ? "text-emerald-400" : objective.performance.status === "warning" ? "text-amber-400" : "text-red-400"}`}>
                      {objective.performance.current_rate}%
                    </span>
                    <span className="text-[9px] text-slate-500">sur {objective.objective.target_rate}%</span>
                  </div>
                </div>
                <div className="mt-2 text-center">
                  <p className={`text-xs font-semibold ${objective.performance.gap >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {objective.performance.gap >= 0 ? `+${objective.performance.gap}% au-dessus` : `${Math.abs(objective.performance.gap)}% en dessous`}
                  </p>
                  <p className="text-[9px] text-slate-500 mt-0.5">
                    {fmt(objective.performance.total_paid)} / {fmt(objective.performance.total_billed)} FCFA
                  </p>
                </div>
              </div>

              {/* Monthly trend vs objective */}
              <div className="col-span-2">
                <p className="text-xs text-slate-500 mb-2">Évolution mensuelle vs objectif</p>
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={objective.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={10} />
                    <YAxis domain={[0, 100]} stroke="#64748b" fontSize={10} tickFormatter={v => `${v}%`} />
                    <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b" }}
                      formatter={(v) => [`${v}%`]} />
                    <Area type="monotone" dataKey="rate" name="Taux" stroke="#0EA5E9" fill="#0EA5E9" fillOpacity={0.15} strokeWidth={2} />
                    {/* Objective line */}
                    <Line type="monotone" dataKey={() => objective.objective.target_rate} name="Objectif" stroke="#F59E0B" strokeDasharray="5 5" strokeWidth={1.5} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Treasury Alerts */}
        {treasury?.alerts?.length > 0 && (
          <div className="space-y-2">
            {treasury.alerts.map((a, i) => {
              const cfg = ALERT_CFG[a.type] || ALERT_CFG.WARNING;
              return (
                <div key={`tres-alert-${i}`} data-testid={`treasury-alert-${i}`} className={`p-4 rounded-xl border ${cfg.bg} flex items-start gap-3`}>
                  <Zap className={`w-5 h-5 mt-0.5 ${cfg.text} flex-shrink-0`} />
                  <div>
                    <p className={`text-sm font-bold ${cfg.text}`}>{a.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{a.message}</p>
                    <p className="text-[10px] text-slate-500 mt-1 italic">{a.action}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Ceiling Alerts */}
        {alerts.length > 0 && (
          <div className="space-y-2">
            {alerts.map((a, i) => {
              const cfg = ALERT_CFG[a.type] || ALERT_CFG.WARNING;
              return (
                <div key={i} data-testid={`alert-${i}`} className={`p-3 rounded-xl border ${cfg.bg} flex items-start gap-3`}>
                  <AlertTriangle className={`w-4 h-4 mt-0.5 ${cfg.text} flex-shrink-0`} />
                  <div>
                    <p className={`text-sm font-semibold ${cfg.text}`}>{a.message}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{a.action}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Ceiling form */}
        {showCeiling && (
          <div className="bg-amber-500/100/5 border border-amber-500/20 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-amber-400 flex items-center gap-2"><Target className="w-4 h-4" /> Définir un plafond de créances</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select value={ceilingForm.insurer_name} onChange={e => setCeilingForm(p => ({ ...p, insurer_name: e.target.value }))}
                className="bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm h-9 px-3 focus:border-amber-500 focus:outline-none" data-testid="ceiling-insurer">
                <option value="">-- Choisir un assureur --</option>
                {registeredInsurers.map(ins => (
                  <option key={ins.id || ins.name} value={ins.name}>{ins.name}</option>
                ))}
              </select>
              <Input type="number" value={ceilingForm.ceiling_amount} onChange={e => setCeilingForm(p => ({ ...p, ceiling_amount: e.target.value }))}
                placeholder="Plafond (FCFA)" className="bg-slate-900/60 border-white/10 text-white text-sm h-9" data-testid="ceiling-amount" />
              <Input type="number" value={ceilingForm.alert_threshold_percent} onChange={e => setCeilingForm(p => ({ ...p, alert_threshold_percent: e.target.value }))}
                placeholder="Seuil alerte (%)" className="bg-slate-900/60 border-white/10 text-white text-sm h-9" data-testid="ceiling-threshold" />
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={saveCeiling} className="bg-amber-500/100 hover:bg-amber-400 text-black font-semibold text-sm" data-testid="save-ceiling">
                <Plus className="w-4 h-4 mr-1" /> Enregistrer le plafond
              </Button>
            </div>
          </div>
        )}

        {/* Monthly sales config - always visible */}
        <div className="bg-sky-500/5 border border-sky-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Info className="w-5 h-5 text-sky-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-sky-400 font-semibold">Chiffre d'affaires mensuel (CA ou recettes)</p>
              <p className="text-xs text-slate-500">
                {treasury?.estimated_sales_set
                  ? `CA renseigné : ${fmt(treasury.monthly_sales)} FCFA — Les créances représentent ${treasury?.sales_vs_credit_ratio || 0}% de vos recettes.`
                  : "Renseignez votre CA mensuel pour calculer le ratio créances/ventes et activer les alertes (seuil 50%)."}
              </p>
            </div>
            <div className="flex gap-2">
              <Input type="number" value={salesForm} onChange={e => setSalesForm(e.target.value)}
                placeholder={treasury?.estimated_sales_set ? fmt(treasury.monthly_sales) : "CA mensuel (FCFA)"}
                className="bg-slate-900/60 border-white/10 text-white text-sm h-9 w-48" data-testid="monthly-sales" />
              <Button onClick={saveSales} className="bg-sky-500 hover:bg-sky-400 text-white text-sm h-9" data-testid="save-sales">
                {treasury?.estimated_sales_set ? "Mettre à jour" : "Enregistrer"}
              </Button>
            </div>
          </div>
          {treasury?.estimated_sales_set && treasury?.sales_vs_credit_ratio >= 50 && (
            <div className="mt-3 bg-amber-500/100/10 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
              <Zap className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-400">Alerte : Les ventes a credit représentent {treasury.sales_vs_credit_ratio}% de votre CA</p>
                <p className="text-[10px] text-slate-500">Sur un CA de {fmt(treasury.monthly_sales)} FCFA, {fmt(treasury.total_outstanding)} FCFA sont en attente de recouvrement chez les assureurs. Seuil d'alerte: 50%.</p>
              </div>
            </div>
          )}
        </div>

        {/* ===== TAB: CREANCES ===== */}
        {activeTab === "créances" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Chart */}
            <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-sky-400" /> Créances par assureur</h3>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                    <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} width={120} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="créances" name="Reste à recouvrer" radius={[0, 4, 4, 0]} maxBarSize={20}>
                      {chartData.map((d, i) => (
                        <Cell key={i} fill={d.plafond > 0 && d.créances >= d.plafond ? "#ef4444" : d.plafond > 0 && d.créances >= d.plafond * 0.8 ? "#f59e0b" : "#3b82f6"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="text-center py-12 text-sm text-slate-500">Aucune creance</div>}
            </div>

            {/* Table */}
            <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5 overflow-auto">
              <h3 className="text-sm font-bold text-white mb-4">Détail par assureur</h3>
              <table className="w-full text-left" data-testid="créances-table">
                <thead><tr className="text-[11px] text-slate-500 border-b border-white/5">
                  <th className="py-2 pr-2">Assureur</th><th className="py-2 px-2">Factures</th>
                  <th className="py-2 px-2 text-red-400">Reste à recouvrer</th>
                  <th className="py-2 px-2 text-emerald-400">Déjà recouvré</th>
                  <th className="py-2 px-2">Plafond</th><th className="py-2">%</th>
                </tr></thead>
                <tbody>
                  {insurers.map((ins, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2.5 pr-2"><span className="text-sm text-white font-medium">{ins.insurer_name}</span></td>
                      <td className="py-2.5 px-2 text-sm text-slate-400">{ins.total_invoices}</td>
                      <td className={`py-2.5 px-2 text-sm font-semibold ${ins.ceiling_reached ? "text-red-400" : "text-red-300"}`}>
                        {fmt(ins.remaining)} <span className="text-[10px] text-slate-600">FCFA</span>
                      </td>
                      <td className="py-2.5 px-2 text-sm font-semibold text-emerald-400">
                        {fmt(ins.paid_total)} <span className="text-[10px] text-slate-600">FCFA</span>
                      </td>
                      <td className="py-2.5 px-2 text-sm text-slate-400">
                        {ins.ceiling_amount > 0 ? `${fmt(ins.ceiling_amount)}` : "-"}
                      </td>
                      <td className="py-2.5">
                        {ins.ceiling_amount > 0 ? (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            ins.ceiling_reached ? "bg-red-500/100/20 text-red-400" :
                            ins.ceiling_alert ? "bg-amber-500/100/20 text-amber-400" :
                            "bg-emerald-500/20 text-emerald-400"
                          }`}>{ins.ceiling_usage_pct}%</span>
                        ) : <span className="text-xs text-slate-600">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ===== TAB: TRESORERIE ===== */}
        {activeTab === "trésorerie" && evolution && (
          <div className="space-y-4">
            {/* Evolution Chart */}
            <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-violet-400" /> Evolution mensuelle — Créances vs Recouvrement
              </h3>
              {(evolution.evolution || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={evolution.evolution}>
                    <defs>
                      <linearGradient id="gradCréances" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradRecouvre" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="créances" name="Reste à recouvrer" stroke="#ef4444" fillOpacity={1} fill="url(#gradCréances)" strokeWidth={2} />
                    <Area type="monotone" dataKey="recouvre" name="Déjà recouvré" stroke="#10b981" fillOpacity={1} fill="url(#gradRecouvre)" strokeWidth={2} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="text-center py-12 text-sm text-slate-500">Pas assez de données pour l'evolution</div>}
            </div>

            {/* Pie Chart by insurer */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5">
                <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-amber-400" /> Répartition par assureur
                </h3>
                {(evolution.by_insurer || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <RechartsPie>
                      <Pie data={evolution.by_insurer} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: "#64748b" }}>
                        {(evolution.by_insurer || []).map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `${fmt(v)} FCFA`} contentStyle={{ background: "#1e293b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} />
                    </RechartsPie>
                  </ResponsiveContainer>
                ) : <div className="text-center py-12 text-sm text-slate-500">Aucune donnee</div>}
              </div>

              {/* Top debtors table */}
              <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5">
                <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-red-400" /> Top assureurs debiteurs
                </h3>
                <div className="space-y-2">
                  {(evolution.by_insurer || []).map((ins, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/40 border border-white/5">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: PIE_COLORS[i % PIE_COLORS.length] + "30", color: PIE_COLORS[i % PIE_COLORS.length] }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate">{ins.name}</p>
                        <p className="text-[10px] text-slate-500">{ins.count} facture{ins.count > 1 ? "s" : ""} en cours</p>
                      </div>
                      <p className="text-sm font-bold text-red-400">{fmt(ins.value)} <span className="text-[10px] text-slate-500">FCFA</span></p>
                    </div>
                  ))}
                  {(evolution.by_insurer || []).length === 0 && <p className="text-center text-sm text-slate-500 py-6">Aucun debiteur</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== ENCAISSEMENTS TAB ===== */}
        {activeTab === "encaissements" && (
          <div className="space-y-5" data-testid="encaissements-tab">
            {/* Overdue Alert Banner */}
            <OverdueBanner forecasts={forecastData?.forecasts || []} fmt={fmt} />
            {/* Summary KPIs */}
            {forecastData && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-1">En attente</p>
                  <p className="text-xl font-bold text-slate-900">{forecastData.total_pending}</p>
                  <p className="text-xs text-slate-500">{fmt(forecastData.total_expected_amount)} FCFA</p>
                  {forecastData.auto_count > 0 && <p className="text-[10px] text-sky-500 mt-1">{forecastData.auto_count} auto / {forecastData.manual_count || 0} manuelles</p>}
                </div>
                <div className="bg-white border border-red-200 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-1">En retard</p>
                  <p className="text-xl font-bold text-red-600">{forecastData.overdue?.count || 0}</p>
                  <p className="text-xs text-red-500">{fmt(forecastData.overdue?.amount || 0)} FCFA</p>
                </div>
                <div className="bg-white border border-amber-200 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-1">Cette semaine</p>
                  <p className="text-xl font-bold text-amber-600">{forecastData.this_week?.count || 0}</p>
                  <p className="text-xs text-amber-500">{fmt(forecastData.this_week?.amount || 0)} FCFA</p>
                </div>
                <div className="bg-white border border-emerald-200 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-1">Ce mois</p>
                  <p className="text-xl font-bold text-emerald-600">{forecastData.this_month?.count || 0}</p>
                  <p className="text-xs text-emerald-500">{fmt(forecastData.this_month?.amount || 0)} FCFA</p>
                </div>
              </div>
            )}

            {/* Info banner about auto-forecasting */}
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-sky-800">Prévisions automatiques</p>
                <p className="text-xs text-sky-600 mt-0.5">
                  Les dates sont calculées : <strong>Date de dépôt + Délai de traitement + Délai de disponibilité chèque/virement</strong>.
                  L'assureur met d'abord le temps de traiter, puis le chèque est disponible après quelques jours supplémentaires.
                </p>
              </div>
            </div>

            {/* Treasury Forecast Chart */}
            {forecastData?.treasury_forecast?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h4 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-violet-500" />
                  Trésorerie prévisionnelle
                </h4>
                <p className="text-xs text-slate-500 mb-4">Montants qui deviendront disponibles chaque semaine (barres vertes) et cumul progressif (barres violettes)</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={forecastData.treasury_forecast} margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} label={{ value: "Semaines", position: "insideBottom", offset: -10, style: { fontSize: 10, fill: "#94a3b8" } }} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} label={{ value: "Montant (FCFA)", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" } }} />
                    <Tooltip
                      formatter={(value, name) => [new Intl.NumberFormat("fr-FR").format(value) + " FCFA", name === "amount" ? "Disponible" : "Cumulé"]}
                      labelFormatter={(label) => {
                        const item = forecastData.treasury_forecast?.find(t => t.week === label);
                        return item ? `Semaine ${label} (${item.period})` : label;
                      }}
                      contentStyle={{ background: "#fff", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                    />
                    <Bar dataKey="amount" fill="#10b981" radius={[4, 4, 0, 0]} name="Disponible" />
                    <Bar dataKey="cumulative" fill="#818cf8" radius={[4, 4, 0, 0]} name="Cumulé" opacity={0.5} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Calendrier visuel des encaissements */}
            <EncaissementsCalendar forecasts={forecastData?.forecasts || []} fmt={fmt} />

            {/* Manual forecast button + form */}
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-600" />
                Prévisions d'encaissement
              </h3>
              <button
                onClick={() => setShowForecastForm(!showForecastForm)}
                className="px-3 py-1.5 rounded-lg bg-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-300"
                data-testid="add-forecast-btn"
              >
                <Plus className="w-3.5 h-3.5 inline mr-1" /> Prévision manuelle
              </button>
            </div>

            {showForecastForm && (
              <div className="bg-white border border-emerald-200 rounded-xl p-5 space-y-3" data-testid="forecast-form">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">ID Facture</label>
                  <input
                    type="text"
                    value={forecastForm.invoice_id}
                    onChange={(e) => setForecastForm({ ...forecastForm, invoice_id: e.target.value })}
                    placeholder="Collez l'ID de la facture..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 bg-slate-50"
                    data-testid="forecast-invoice-id"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Date d'encaissement prévue</label>
                  <input
                    type="date"
                    value={forecastForm.expected_date}
                    onChange={(e) => setForecastForm({ ...forecastForm, expected_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 bg-slate-50"
                    data-testid="forecast-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Notes</label>
                  <input
                    type="text"
                    value={forecastForm.notes}
                    onChange={(e) => setForecastForm({ ...forecastForm, notes: e.target.value })}
                    placeholder="Ex: Accord verbal du 15/03..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 bg-slate-50"
                    data-testid="forecast-notes"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={createForecast} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700" data-testid="forecast-submit">
                    Créer la prévision
                  </button>
                  <button onClick={() => setShowForecastForm(false)} className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm hover:bg-slate-50">
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Auto + Manual Forecasts List */}
            {forecastData?.forecasts?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="forecasts-list">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[11px] text-slate-500 border-b border-slate-200 bg-slate-50">
                      <th className="py-3 px-4">Facture</th>
                      <th className="py-3 px-3">Assureur</th>
                      <th className="py-3 px-3">Montant</th>
                      <th className="py-3 px-3">Traitement</th>
                      <th className="py-3 px-3">Disponibilité</th>
                      <th className="py-3 px-3">Statut</th>
                      <th className="py-3 px-3">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecastData.forecasts.map((fc, i) => (
                      <tr key={fc.id || i} className="border-b border-slate-100 hover:bg-slate-50/80" data-testid={`forecast-row-${i}`}>
                        <td className="py-3 px-4">
                          <p className="text-sm font-medium text-slate-900">{fc.invoice_number || fc.invoice_id?.slice(0, 8)}</p>
                          {fc.notes && <p className="text-[10px] text-slate-400 mt-0.5">{fc.notes}</p>}
                        </td>
                        <td className="py-3 px-3 text-sm text-slate-700">{fc.insurer_name || "-"}</td>
                        <td className="py-3 px-3 text-sm font-semibold text-slate-900">{fmt(fc.expected_amount)} <span className="text-[10px] text-slate-400">FCFA</span></td>
                        <td className="py-3 px-3">
                          <p className="text-sm text-slate-800">{new Date(fc.expected_date + "T00:00:00").toLocaleDateString("fr-FR")}</p>
                          <p className="text-[10px] text-slate-400">{fc.delay_days || "-"}j délai</p>
                        </td>
                        <td className="py-3 px-3">
                          {fc.availability_date ? (
                            <>
                              <p className="text-sm font-semibold text-emerald-700">{new Date(fc.availability_date + "T00:00:00").toLocaleDateString("fr-FR")}</p>
                              <p className="text-[10px] text-emerald-500">+{fc.check_availability_days || 3}j après traitement</p>
                            </>
                          ) : (
                            <p className="text-sm text-slate-500">{new Date(fc.expected_date + "T00:00:00").toLocaleDateString("fr-FR")}</p>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            fc.is_overdue ? "bg-red-100 text-red-700" : fc.days_remaining !== undefined && fc.days_remaining <= 7 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                          }`}>
                            {fc.is_overdue ? `Retard ${fc.days_overdue}j` : fc.days_remaining !== undefined && fc.days_remaining <= 7 ? "Imminent" : "A venir"}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                            fc.source === "auto" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"
                          }`}>
                            {fc.source === "auto" ? "Auto" : "Manuel"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* By insurer breakdown */}
            {forecastData?.by_insurer?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Par assureur</h4>
                <div className="space-y-2">
                  {forecastData.by_insurer.map((ins, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-4 h-4 text-sky-600" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{ins.insurer}</p>
                          <p className="text-xs text-slate-500">{ins.count} facture(s) en attente</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900">{fmt(ins.amount)} FCFA</p>
                        {ins.overdue > 0 && (
                          <p className="text-xs text-red-500">{ins.overdue} en retard</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!forecastData || forecastData.total_pending === 0) && (
              <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
                <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Aucune prévision d'encaissement</p>
                <p className="text-xs text-slate-400 mt-1">Les prévisions apparaîtront automatiquement dès qu'une facture sera déposée</p>
              </div>
            )}
          </div>
        )}
      </div>
    </LogLayout>
  );
}
